import React, { useEffect, useMemo, useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import Button from '../ui/Button';
import StatusBanner from '../ui/StatusBanner';
import CalendarDayCell from './CalendarDayCell';
import './SharedTimeOffCalendar.css';
import { DIRECTOR_DATA_UPDATED_EVENT } from '../../utils/directorEvents';
import { buildQuery } from '../../utils/buildQuery';

function formatMonthLabel(month, year) {
  return new Date(year, month - 1, 1).toLocaleString(undefined, {
    month: 'long',
    year: 'numeric'
  });
}

function toDateKey(date) {
  return date.toISOString().slice(0, 10);
}

function toUtcDate(value) {
  if (!value) {
    return null;
  }

  const normalizedValue = String(value);
  const parsed = normalizedValue.includes('T')
    ? new Date(normalizedValue)
    : new Date(`${normalizedValue}T00:00:00`);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildMonthCells(month, year) {
  const firstOfMonth = new Date(year, month - 1, 1);
  const startOffset = firstOfMonth.getDay();
  const gridStart = new Date(year, month - 1, 1 - startOffset);
  const cells = [];

  for (let index = 0; index < 42; index += 1) {
    const cellDate = new Date(gridStart);
    cellDate.setDate(gridStart.getDate() + index);
    cells.push({
      dateKey: toDateKey(cellDate),
      dayNumber: cellDate.getDate(),
      inCurrentMonth: cellDate.getMonth() === month - 1
    });
  }

  return cells;
}

function buildEntriesByDate(entries) {
  return entries.reduce((accumulator, entry) => {
    const start = toUtcDate(entry.startDate);
    const end = toUtcDate(entry.endDate || entry.startDate);
    if (!start || !end) {
      return accumulator;
    }

    for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
      const key = toDateKey(cursor);
      if (!accumulator[key]) {
        accumulator[key] = [];
      }

      accumulator[key].push(entry);
    }

    return accumulator;
  }, {});
}

function SharedTimeOffCalendar({
  locationCode,
  locationLabel,
  onOpenPersonWorkspace = null,
  roleLabel,
  title = 'Shared Time-Off Calendar',
  subtitle
}) {
  const today = new Date();
  const [visibleMonth, setVisibleMonth] = useState(today.getMonth() + 1);
  const [visibleYear, setVisibleYear] = useState(today.getFullYear());

  const calendarUrl = useMemo(() => {
    const qs = buildQuery({
      month: visibleMonth,
      year: visibleYear,
      locationCode: locationCode && locationCode !== 'ALL' ? locationCode : null
    });
    return `/api/calendar?${qs}`;
  }, [locationCode, visibleMonth, visibleYear]);

  const requestOptions = useMemo(
    () => ({ timeoutMs: locationCode === 'ALL' ? 25000 : 18000 }),
    [locationCode]
  );

  const {
    data: fetchedData,
    isLoading,
    error,
    refetch
  } = useFetch(calendarUrl, {
    deps: [locationCode, visibleMonth, visibleYear],
    requestOptions
  });

  const calendarData = useMemo(() => ({
    events: Array.isArray(fetchedData?.events) ? fetchedData.events : [],
    blackoutPolicies: Array.isArray(fetchedData?.blackoutPolicies) ? fetchedData.blackoutPolicies : [],
    coveragePolicies: Array.isArray(fetchedData?.coveragePolicies) ? fetchedData.coveragePolicies : []
  }), [fetchedData]);

  const errorMessage = error || '';

  useEffect(() => {
    const handleDirectorDataUpdated = () => {
      refetch();
    };

    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);

    return () => {
      window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    };
  }, [refetch]);

  const monthCells = useMemo(
    () => buildMonthCells(visibleMonth, visibleYear),
    [visibleMonth, visibleYear]
  );

  const eventsByDate = useMemo(
    () => buildEntriesByDate(calendarData.events),
    [calendarData.events]
  );

  const blackoutByDate = useMemo(
    () => buildEntriesByDate(calendarData.blackoutPolicies),
    [calendarData.blackoutPolicies]
  );

  const coverageByDate = useMemo(
    () => buildEntriesByDate(calendarData.coveragePolicies),
    [calendarData.coveragePolicies]
  );

  const handlePreviousMonth = () => {
    if (visibleMonth === 1) {
      setVisibleMonth(12);
      setVisibleYear(current => current - 1);
      return;
    }

    setVisibleMonth(current => current - 1);
  };

  const handleNextMonth = () => {
    if (visibleMonth === 12) {
      setVisibleMonth(1);
      setVisibleYear(current => current + 1);
      return;
    }

    setVisibleMonth(current => current + 1);
  };

  return (
    <div className="role-section">
      <div className="role-section-header">
        <h2>{title}</h2>
        <span>{locationLabel}</span>
      </div>
      <p className="role-section-copy">
        {subtitle || `${roleLabel} calendar for approved time off, attendance events, blackout dates, and coverage rules.`}
      </p>

      <div className="shared-calendar-toolbar">
        <Button variant="secondary" onClick={handlePreviousMonth}>
          Previous
        </Button>
        <div className="shared-calendar-toolbar-summary">
          <strong>{formatMonthLabel(visibleMonth, visibleYear)}</strong>
          <span>{calendarData.events.length} approved events</span>
        </div>
        <Button variant="secondary" onClick={handleNextMonth}>
          Next
        </Button>
      </div>

      <div className="shared-calendar-legend">
        <span className="shared-calendar-legend-item event">Time Off / Attendance</span>
        <span className="shared-calendar-legend-item blackout">Blackout Day</span>
        <span className="shared-calendar-legend-item coverage">Coverage Rule</span>
      </div>

      <StatusBanner
        kind="loading"
        message={isLoading ? `Loading shared calendar for ${locationLabel}...` : ''}
      />
      <StatusBanner kind="error" message={errorMessage} onRetry={refetch} />


      {!isLoading && !errorMessage && (
        <div className="shared-calendar-shell">
          <div className="shared-calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div className="shared-calendar-weekday" key={day}>{day}</div>
            ))}
          </div>
          <div className="shared-calendar-grid">
            {monthCells.map(cell => (
              <CalendarDayCell
                blackoutEntries={blackoutByDate[cell.dateKey] || []}
                coverageEntries={coverageByDate[cell.dateKey] || []}
                date={cell.dateKey}
                dayEvents={eventsByDate[cell.dateKey] || []}
                dayNumber={cell.dayNumber}
                inCurrentMonth={cell.inCurrentMonth}
                key={cell.dateKey}
                onOpenPersonWorkspace={onOpenPersonWorkspace}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default SharedTimeOffCalendar;
